import { Alert, AlertTitle, Box, Button, LinearProgress, Typography } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { SubscriptionStatus } from '../../types';

export const SubscriptionBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const subscription = useAuthStore((state) => state.subscription);
  // Session-only dismiss: resets on page reload, login/logout — but not on navigation
  const [dismissed, setDismissed] = useState(false);

  if (!subscription) return null;

  const { status, trial_days_remaining } = subscription;

  // --- Free trial: show banner only in the last 7 days ---
  if (status === SubscriptionStatus.TRIALING) {
    const days = trial_days_remaining ?? 30;
    if (days > 7 || dismissed) return null;

    const progress = Math.max(0, Math.min(100, ((30 - days) / 30) * 100));

    return (
      <Alert
        severity="warning"
        icon={<TimerIcon />}
        onClose={() => setDismissed(true)}
        sx={{ borderRadius: 0, mb: 0, alignItems: 'center' }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => navigate('/subscription')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('subscription.setupPayment')}
            </Button>
          </Box>
        }
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {t('subscription.trialDaysLeft', { days })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            color="warning"
            sx={{ mt: 0.5, height: 4, borderRadius: 2, width: 200, maxWidth: '100%' }}
          />
        </Box>
      </Alert>
    );
  }

  if (status === SubscriptionStatus.PAST_DUE) {
    // Distinguish trial expiry (no Stripe) from actual payment failure
    const isTrialExpired = !subscription.stripe_subscription_id;
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: 0, mb: 0 }}
        action={
          <Button size="small" color="inherit" variant="outlined" onClick={() => navigate('/subscription')}>
            {t('subscription.subscribe')}
          </Button>
        }
      >
        <AlertTitle>{isTrialExpired ? t('subscription.trialExpiredTitle') : t('subscription.pastDueTitle')}</AlertTitle>
        {isTrialExpired ? t('subscription.trialExpiredMessage') : t('subscription.pastDueMessage')}
      </Alert>
    );
  }

  if (status === SubscriptionStatus.CANCELED || status === SubscriptionStatus.UNPAID) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: 0, mb: 0 }}
        action={
          <Button size="small" color="inherit" variant="outlined" onClick={() => navigate('/subscription')}>
            {t('subscription.reactivate')}
          </Button>
        }
      >
        <AlertTitle>{t('subscription.canceledTitle')}</AlertTitle>
        {t('subscription.canceledMessage')}
      </Alert>
    );
  }

  return null;
};
